import { useState } from "react";
import { motion } from "framer-motion";
import { Anchor, Building2, Store } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import SupplierEnquiryForm from "../components/SupplierEnquiryForm";
import FranchiseEnquiryForm from "../components/FranchiseEnquiryForm";
import B2bEnquiryForm from "../components/B2bEnquiryForm";
import PartnershipFaqAccordion from "../components/PartnershipFaqAccordion";
import Breadcrumbs from "../components/Breadcrumbs";
import { trackEvent } from "../utils/analytics";
import useDocumentTitle from "../hooks/useDocumentTitle";
import type { EnquiryType } from "../data/enquiryTypes";

type FormTab = Exclude<EnquiryType, "general">;

const TABS: { id: FormTab; label: string; icon: typeof Anchor; description: string }[] = [
  {
    id: "supplier",
    label: "Become a Supplier",
    icon: Anchor,
    description: "Sell your catch directly, at a price you know before you leave the dock.",
  },
  {
    id: "franchise",
    label: "Franchise / City Partner",
    icon: Store,
    description: "Bring ZenFresh to a new city as a local partner.",
  },
  {
    id: "b2b",
    label: "Corporate & Bulk Orders",
    icon: Building2,
    description: "Recurring, traceable seafood supply for hotels, restaurants, and caterers.",
  },
];

export default function Partnerships() {
  useDocumentTitle("Partner With Us");
  const [activeTab, setActiveTab] = useState<FormTab>("supplier");

  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[10%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Partnerships &amp; Enquiries
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Partner With Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Whether you fish, cook, sell, or invest — there&apos;s a direct line into ZenFresh for you.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-3xl px-6 py-24">
        <ScrollReveal className="text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
            Tell Us About Your Business
          </h2>
          <p className="mt-3 text-ocean-800/70 dark:text-foam-100/70">
            Pick the enquiry type that fits — it goes straight to the right team.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.1} className="mt-10">
          <div className="grid gap-3 sm:grid-cols-3">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  trackEvent("partnership_tab_select", { tab: tab.id });
                }}
                aria-pressed={activeTab === tab.id}
                className={`rounded-2xl p-4 text-left ring-1 transition-colors ${
                  activeTab === tab.id
                    ? "bg-teal-400/15 ring-teal-400"
                    : "bg-white ring-ocean-900/5 hover:bg-foam-50 dark:bg-ocean-900 dark:ring-white/10 dark:hover:bg-white/5"
                }`}
              >
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                    activeTab === tab.id ? "bg-teal-400 text-ocean-950" : "bg-ocean-900/5 text-ocean-800 dark:bg-white/10 dark:text-foam-100"
                  }`}
                >
                  <tab.icon className="h-4 w-4" strokeWidth={1.5} />
                </div>
                <p className="mt-2 text-sm font-semibold text-ocean-950 dark:text-foam-50">{tab.label}</p>
                <p className="mt-1 text-xs text-ocean-800/60 dark:text-foam-100/60">{tab.description}</p>
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10 sm:p-8">
            {activeTab === "supplier" && <SupplierEnquiryForm />}
            {activeTab === "franchise" && <FranchiseEnquiryForm />}
            {activeTab === "b2b" && <B2bEnquiryForm />}
          </div>
        </ScrollReveal>
      </section>

      <section id="faq" className="scroll-mt-24 bg-foam-100 py-24 dark:bg-ocean-950">
        <div className="mx-auto max-w-3xl px-6">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
          <div className="mt-12">
            <PartnershipFaqAccordion />
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
